import React, { useEffect, useState } from 'react';
import { Table, Modal, Button, Toast } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AddPantryIngredientButton from '../components/AddPantryIngredientButton';
import { useDeletePantryIngredient } from '../hooks/pantryIngredient';
import { useUpdatePantryIngredient } from '../hooks/pantryIngredient';
import { useAddPantryIngredients } from '../hooks/pantryIngredient';
import { useAuth } from '../context/AuthContext';

import { addCsrfHeader } from '../utils/csrfUtils';
import ErrorBoundary from '../components/ErrorBoundary';
import useUpdateRelevantGroceryListIngredientsOnPantryIngredientCRUD from '../hooks/updateRelevantGroceryListIngredientsOnPantryIngredientCRUD';
import { getLocalDateString } from '../utils/dateUtils';
import { getIngredientImageUrl } from '../utils/imageUtils';

const PantryIngredients = () => {
    const navigate = useNavigate();
    const { pgUser, sessionToken } = useAuth();
    
    // Hook declarations
    const { deletePantryIngredient, isDeleting, error: deleteError } = useDeletePantryIngredient();
    const { updatePantryIngredient, isUpdating, error: updateError } = useUpdatePantryIngredient();
    const { deleteMultiplePantryIngredients, isDeleting: isDeletingMultiple } = useAddPantryIngredients();
    const { updateGroceryLists } = useUpdateRelevantGroceryListIngredientsOnPantryIngredientCRUD();
    
    // State declarations first
    const [isAuthLoading, setIsAuthLoading] = useState(true);
    const [pantryId, setPantryId] = useState(null);
    const [pantryIngredients, setPantryIngredients] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [editingQuantityId, setEditingQuantityId] = useState(null);
    const [editingExpirationId, setEditingExpirationId] = useState(null);
    const [editingQuantity, setEditingQuantity] = useState('');
    const [editingExpiration, setEditingExpiration] = useState('');
    const [originalExpiration, setOriginalExpiration] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDeleteMultipleModal, setShowDeleteMultipleModal] = useState(false);
    const [showDeleteAllButton, setShowDeleteAllButton] = useState(false);
    const [ingredientToDelete, setIngredientToDelete] = useState(null);
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [errorModifyingPantryIngredient, setErrorModifyingPantryIngredient] = useState(false);
    const [errorModifyingPantryIngredientMessage, setErrorModifyingPantryIngredientMessage] = useState('');
    const [toastMessages, setToastMessages] = useState([]);
    const [toastCounter, setToastCounter] = useState(0);  // Add counter for unique IDs
    const [failedImageIds, setFailedImageIds] = useState(new Set());

    // Function to generate a unique ID
    const generateUniqueId = () => {
        return Math.random().toString(36).substring(2) + Date.now().toString(36);
    };

    // Function to show success toast notification
    const handleGroceryListUpdate = (message) => {
        const newToastId = generateUniqueId();
        
        // Add new toast to the list
        setToastMessages(prev => [...prev, { id: newToastId, message }]);
        
        // Remove this specific toast after 3 seconds
        setTimeout(() => {
            setToastMessages(prev => prev.filter(toast => toast.id !== newToastId));
        }, 3000);
    };

    // Initialize pgUser-dependent state
    useEffect(() => {
        if (!pgUser) {
            setIsAuthLoading(false);
            return;
        }
        setPantryId(pgUser?.pantry_id || null);
        setIsAuthLoading(false);
    }, [pgUser]);

    // Fetch pantry ingredients
    useEffect(() => {
        const fetchPantryIngredients = async () => {
            if (!pantryId || !pgUser?.id) return;
            
            setIsLoading(true);
            setError(null);
            
            try {
                if (!sessionToken) {
                    throw new Error('No valid session found');
                }

                const headers = await addCsrfHeader({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                });

                const response = await fetch('/api/v1/pantry-ingredient/pantry/' + pantryId + '/app_user/' + pgUser.id, {
                    headers
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Failed to fetch pantry ingredients');
                }

                const data = await response.json();
                setPantryIngredients(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Error loading pantry:', err);
                setError(err.message);
                setPantryIngredients([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPantryIngredients();
    }, [pantryId, pgUser?.id]);

    // Calculate days until expiration
    const getDaysUntilExpiration = (expirationDate) => {
        if (!expirationDate) return null;
        const today = new Date();
        const expDate = new Date(expirationDate);
        const diffTime = expDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    // Function to format the expiration display
    const formatExpiration = (days) => {
        if (days === null) return '';
        if (days === 0) return 'Expires today';
        if (days < 0) return `${Math.abs(days)} days ago`;
        return `${days} days`;
    };

    // Add this helper function
    const formatQuantity = (quantity) => {
        // Convert to number in case it's a string
        const num = Number(quantity);
        // Check if it's a whole number
        if (Number.isInteger(num)) {
            return num.toString();
        }
        // Remove trailing zeros after decimal
        return num.toString().replace(/\.?0+$/, '');
    };

    // Filter ingredients based on search query, with null check
    const filteredPantryIngredients = pantryIngredients.filter(ingredient =>
        ingredient?.ingredient_name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false
    );

    // Update showDeleteAllButton when pantryIngredients changes
    useEffect(() => {
        setShowDeleteAllButton(pantryIngredients.length > 1);
    }, [pantryIngredients]);

    // Sorting function
    const sortIngredients = (ingredients) => {
        if (!sortField) return ingredients;

        return [...ingredients].sort((a, b) => {
            let aValue, bValue;

            switch (sortField) {
                case 'ingredient_name':
                    aValue = a.ingredient_name.toLowerCase();
                    bValue = b.ingredient_name.toLowerCase();
                    break;
                case 'primary_food_group':
                    aValue = (a.primary_food_group || '').toLowerCase();
                    bValue = (b.primary_food_group || '').toLowerCase();
                    break;
                case 'quantity':
                    aValue = Number(a.quantity) || 0;
                    bValue = Number(b.quantity) || 0;
                    break;
                case 'expiration_date':
                    aValue = a.expiration_date ? new Date(a.expiration_date) : new Date('9999-12-31');
                    bValue = b.expiration_date ? new Date(b.expiration_date) : new Date('9999-12-31');
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    };

    // Handle column header click for sorting
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Get sort indicator for column headers
    const getSortIndicator = (field) => {
        if (sortField !== field) return <img src="/icons/arrows-up-down.svg" alt="Sort" style={{ width: '16px', height: '16px', marginLeft: '4px' }} />;
        return sortDirection === 'asc' ? '↑' : '↓';
    };

    // Apply sorting to filtered ingredients
    const sortedAndFilteredIngredients = sortIngredients(filteredPantryIngredients);

    const handleQuantityEdit = (ingredient) => {
        setEditingQuantityId(ingredient.id);
        setEditingQuantity(formatQuantity(ingredient.quantity));
    };

    const handleQuantitySubmit = async (ingredient) => {
        try {
            const result = await updatePantryIngredient(ingredient.id, pantryId, pgUser.id, {
                quantity: Number(editingQuantity)
            });
            if (result) {
                setPantryIngredients(pantryIngredients.map(item => 
                    item.id === ingredient.id 
                        ? { ...item, quantity: Number(editingQuantity) }
                        : item
                ));

                // Update relevant grocery lists
                const updateSuccess = await updateGroceryLists(ingredient.ingredient_id, pgUser.id, handleGroceryListUpdate);
                if (!updateSuccess) {
                    console.error('Failed to update grocery lists after quantity change');
                }
            } else {
                setErrorModifyingPantryIngredient(true);
                setErrorModifyingPantryIngredientMessage('Error updating pantry item. Please refresh the page and try again.');
            }
        } catch (error) {
            console.error('Error updating quantity:', error);
            setErrorModifyingPantryIngredient(true);
            setErrorModifyingPantryIngredientMessage('Error updating pantry item. Please refresh the page and try again.');
        }
        setEditingQuantityId(null);
    };

    const handleQuantityKeyPress = (e, ingredient) => {
        if (e.key === 'Enter') {
            handleQuantitySubmit(ingredient);
        } else if (e.key === 'Escape') {
            setEditingQuantityId(null);
        }
    };

    const handleExpirationEdit = (ingredient) => {
        setEditingExpirationId(ingredient.id);
        // Format the date to YYYY-MM-DD for the date input
        if (ingredient.expiration_date) {
            const date = new Date(ingredient.expiration_date + 'T12:00:00');
            const formattedDate = getLocalDateString(date);
            setEditingExpiration(formattedDate);
            setOriginalExpiration(formattedDate);
        } else {
            setEditingExpiration('');
            setOriginalExpiration('');
        }
    };

    const handleExpirationSubmit = async (ingredient) => {
        // Only submit if the value has actually changed
        if (editingExpiration === originalExpiration) {
            setEditingExpirationId(null);
            return;
        }

        try {
            const result = await updatePantryIngredient(ingredient.id, pantryId, pgUser.id, {
                expiration_date: editingExpiration || null
            });
            
            if (result) {
                setPantryIngredients(pantryIngredients.map(item => 
                    item.id === ingredient.id 
                        ? { ...item, expiration_date: editingExpiration }
                        : item
                ));
            }
        } catch (error) {
            console.error('Error updating expiration date:', error);
            setErrorModifyingPantryIngredient(true);
            setErrorModifyingPantryIngredientMessage('Error updating pantry item. Please refresh the page and try again.');
        }
        setEditingExpirationId(null);
    };

    const handleExpirationKeyPress = (e, ingredient) => {
        if (e.key === 'Enter') {
            handleExpirationSubmit(ingredient);
        } else if (e.key === 'Escape') {
            setEditingExpirationId(null);
        }
    };

    const handleDeleteClick = (ingredient) => {
        setIngredientToDelete(ingredient);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (ingredientToDelete) {
            const success = await deletePantryIngredient(ingredientToDelete.id, pantryId, pgUser.id);
            if (success) {
                setPantryIngredients(pantryIngredients.filter(item => item.id !== ingredientToDelete.id));

                // Update relevant grocery lists after deleting the pantry ingredient
                const updateSuccess = await updateGroceryLists(ingredientToDelete.ingredient_id, pgUser.id, handleGroceryListUpdate);
                if (!updateSuccess) {
                    console.error('Failed to update grocery lists after ingredient deletion');
                }

                setShowDeleteModal(false);
                setIngredientToDelete(null);
            } else {
                setErrorModifyingPantryIngredient(true);
                setErrorModifyingPantryIngredientMessage('Error deleting pantry item. Please refresh the page and try again.');
            }
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteModal(false);
        setIngredientToDelete(null);
    };

    const handleIngredientClick = (ingredientId) => {
        navigate(`/ingredients/${ingredientId}`);
    };

    const handlePantryIngredientAdded = async () => {
        // Refetch pantry ingredients after adding a new one
        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            // Add CSRF token and auth header
            const headers = await addCsrfHeader({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            });

            const response = await fetch(`/api/v1/pantry-ingredient/pantry/${pantryId}/app_user/${pgUser.id}`, {
                headers
            });
            
            if (!response.ok) throw new Error('Failed to fetch updated pantry ingredients');
            const updatedIngredients = await response.json();
            setPantryIngredients(updatedIngredients);
        } catch (error) {
            console.error('Error fetching updated pantry ingredients:', error);
        }
    };

    const handleDeleteMultipleCancel = () => {
        setShowDeleteMultipleModal(false);
    };

    const handleDeleteMultipleConfirm = async () => {
        // Get all pantry ingredient IDs
        const pantryIngredientIds = pantryIngredients.map(ingredient => ingredient.id);
        
        // Call the delete multiple function
        const success = await deleteMultiplePantryIngredients(pantryIngredientIds, pgUser.id);
        
        if (success) {
            // Clear the pantry ingredients list
            setPantryIngredients([]);
            setShowDeleteMultipleModal(false);
        }
    };

    return (
        <ErrorBoundary fallbackMessage="Unable to load your pantry. Please try again later.">
            <div className="container">
                {isAuthLoading ? (
                    <div className="text-center my-4">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : !pgUser ? (
                    <div className="alert alert-warning">
                        <p className="h5 mb-3">Unable to connect to the server. Please try again later or send a message to <a href="mailto:support@getautochef.com">support@getautochef.com</a></p>
                        <Button onClick={() => window.location.reload()}>Retry</Button>
                    </div>
                ) : (
                    <>
                        <div className="row mb-4">
                            <div className="col">
                                <h1>My Pantry</h1>
                            </div>
                        </div>

                        {(!pgUser.setup_complete) && (
                            <div className="alert alert-warning">
                                <p className="h5 mb-3">Complete meal preferences to start adding ingredients to your pantry!</p>
                                <Button onClick={() => navigate('/setup')}>Complete Setup</Button>
                            </div>
                        )}

                        {pgUser.setup_complete && !pgUser.has_access && (
                            <div className="alert alert-warning">
                                <p className="h5 mb-3">Finish your account setup to start adding ingredients to your pantry!</p>
                                <Button onClick={() => navigate('/pricing')}>Complete Setup</Button>
                            </div>
                        )}

                        {pgUser.setup_complete && pgUser.has_access && (
                            <div className="mb-4" style={{ textAlign: 'left' }}>
                                {/* <input
                                    type="text"
                                    placeholder="Search pantry..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    className="form-control-custom form-control mb-3"
                                /> */}
                                
                                <AddPantryIngredientButton
                                    pantryId={pantryId}
                                    appUserId={pgUser.id}
                                    onSubmit={handlePantryIngredientAdded}
                                    onGroceryListUpdate={handleGroceryListUpdate}
                                    buttonText="+ Add Ingredient to Pantry"
                                />
                            </div>
                        )}

                        {isLoading ? (
                            <div className="text-center my-4">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : error ? (
                            <div className="alert alert-danger px-2 py-2" role="alert">
                                Error loading pantry. Please refresh the page and try again.
                            </div>
                        ) : (
                            <Table hover responsive className="pantry-table">
                                <thead>
                                    <tr>
                                        <th 
                                            onClick={() => handleSort('ingredient_name')}
                                            style={{ cursor: 'pointer', userSelect: 'none', marginLeft: '0px' }}
                                        >
                                            Ingredient {getSortIndicator('ingredient_name')}
                                        </th>
                                        <th 
                                            onClick={() => handleSort('primary_food_group')}
                                            style={{ cursor: 'pointer', userSelect: 'none', marginLeft: '0px' }}
                                        >
                                            Food Group {getSortIndicator('primary_food_group')}
                                        </th>
                                        <th 
                                            onClick={() => handleSort('quantity')}
                                            style={{ cursor: 'pointer', userSelect: 'none', marginLeft: '0px' }}
                                        >
                                            Amount {getSortIndicator('quantity')}
                                        </th>
                                        <th 
                                            onClick={() => handleSort('expiration_date')}
                                            style={{ cursor: 'pointer', userSelect: 'none', marginLeft: '0px' }}
                                        >
                                            Expiration {getSortIndicator('expiration_date')}
                                        </th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedAndFilteredIngredients.map(ingredient => {
                                        const daysUntilExpiration = getDaysUntilExpiration(ingredient.expiration_date);

                                        return (
                                            <tr key={ingredient.id}>
                                                <td>
                                                    <div className="d-flex align-items-center" 
                                                         onClick={() => handleIngredientClick(ingredient.ingredient_id)}
                                                         style={{ cursor: 'pointer' }}
                                                    >
                                                        {!ingredient.ingredient_id || failedImageIds.has(ingredient.ingredient_id) ? (
                                                            <div 
                                                                style={{ width: '25px', height: '25px', borderRadius: '4px', marginRight: '10px', backgroundColor: '#e9ecef', flexShrink: 0 }} 
                                                                aria-hidden 
                                                            />
                                                        ) : (
                                                            <img 
                                                                src={getIngredientImageUrl(ingredient.ingredient_id)}
                                                                alt={ingredient.ingredient_name}
                                                                style={{ width: '25px', height: '25px', borderRadius: '4px', marginRight: '10px' }}
                                                                onError={() => setFailedImageIds(prev => new Set([...prev, ingredient.ingredient_id]))}
                                                            />
                                                        )}
                                                        {ingredient.ingredient_name}
                                                    </div>
                                                </td>
                                                <td>
                                                    {ingredient.primary_food_group}
                                                </td>
                                                <td>
                                                    {editingQuantityId === ingredient.id ? (
                                                        <div className="d-flex align-items-center">
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm quantity-input"
                                                                value={editingQuantity}
                                                                onChange={(e) => setEditingQuantity(e.target.value)}
                                                                onBlur={() => handleQuantitySubmit(ingredient)}
                                                                onKeyDown={(e) => handleQuantityKeyPress(e, ingredient)}
                                                                autoFocus
                                                                min="0"
                                                                step="0.1"
                                                            />
                                                            <span className="ms-2">{ingredient.unit_of_measurement}</span>
                                                        </div>
                                                    ) : (
                                                        <span 
                                                            onClick={() => handleQuantityEdit(ingredient)}
                                                            className="editable-quantity"
                                                        >
                                                            {formatQuantity(ingredient.quantity)} {ingredient.unit_of_measurement}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className={`expiration-cell ${daysUntilExpiration < 0 ? 'text-danger' : 
                                                                     daysUntilExpiration <= 7 ? 'text-warning' : 
                                                                     daysUntilExpiration === null ? 'text-muted' :
                                                                     'text-success'}`}>
                                                    {editingExpirationId === ingredient.id ? (
                                                        <div className="d-flex align-items-center">
                                                            <input
                                                                type="date"
                                                                className="form-control form-control-sm"
                                                                value={editingExpiration}
                                                                onChange={(e) => setEditingExpiration(e.target.value)}
                                                                onBlur={() => handleExpirationSubmit(ingredient)}
                                                                onKeyDown={(e) => handleExpirationKeyPress(e, ingredient)}
                                                                autoFocus
                                                            />
                                                        </div>
                                                    ) : (
                                                        <span 
                                                            onClick={() => handleExpirationEdit(ingredient)}
                                                            className="editable-quantity"
                                                            style={{ 
                                                                cursor: 'pointer',
                                                                display: 'block',
                                                                padding: '4px',
                                                                minHeight: '28px'
                                                            }}
                                                        >
                                                            {formatExpiration(daysUntilExpiration)}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-2">
                                                        <button 
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleDeleteClick(ingredient)}
                                                            disabled={isDeleting}
                                                        >
                                                            <img src="/icons/trash.svg" alt="Delete" />
                                                        </button>
                                                        {/* <button  */}
                                                            {/* className="btn btn-sm btn-outline-success" */}
                                                            {/* onClick={() => handle add to cart} */}
                                                        {/* > */}
                                                            {/* <img src="/icons/cart-plus.svg" alt="Add to cart" /> */}
                                                        {/* </button> */}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        )}

                        {showDeleteAllButton && (
                            <button 
                                className="btn btn-outline-danger mb-3"
                                onClick={() => setShowDeleteMultipleModal(!showDeleteMultipleModal)}
                                disabled={showDeleteMultipleModal || isDeletingMultiple || pantryIngredients.length === 0}
                            >
                                {isDeletingMultiple ? 'Deleting...' : 'Delete All Items'}
                            </button>
                        )}



                        {/* Delete Multiple Ingredients Modal */}
                        <Modal show={showDeleteMultipleModal} onHide={handleDeleteMultipleCancel}>
                            <Modal.Header closeButton>
                                <Modal.Title>Confirm Deletion</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                Are you sure you want to delete all ingredients in your pantry?
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={handleDeleteMultipleCancel} disabled={isDeletingMultiple}>
                                    Cancel
                                </Button>
                                <Button 
                                    variant="danger" 
                                    onClick={handleDeleteMultipleConfirm}
                                    disabled={isDeletingMultiple}
                                >
                                    {isDeletingMultiple ? 'Deleting...' : 'Delete All Items'}
                                </Button>
                            </Modal.Footer>
                        </Modal>

                        
                        {/* Delete Confirmation Modal */}
                        <Modal show={showDeleteModal} onHide={handleDeleteCancel}>
                            <Modal.Header closeButton>
                                <Modal.Title>Confirm Deletion</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                Remove {ingredientToDelete?.ingredient_name} from your pantry?
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={handleDeleteCancel}>
                                    Cancel
                                </Button>
                                <Button 
                                    variant="danger" 
                                    onClick={handleDeleteConfirm}
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? 'Deleting...' : 'Delete'}
                                </Button>
                            </Modal.Footer>
                        </Modal>
                        
                        {/* Error Modifying Pantry Ingredient Toast Notification */}
                        <Toast 
                            show={errorModifyingPantryIngredient} 
                            onClose={() => setErrorModifyingPantryIngredient(false)}
                            style={{
                                position: 'fixed',
                                top: '10px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                zIndex: 9999,
                                backgroundColor: '#dc3545',
                                border: 'none',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                width: '50%'
                            }}
                        >
                            <Toast.Header style={{ 
                                backgroundColor: '#dc3545', 
                                borderBottom: '1px solid rgba(255,255,255,0.2)',
                                display: 'flex',
                                justifyContent: 'center',
                                position: 'relative',
                                width: '100%'
                            }}>
                                <strong style={{ color: 'white' }}>{errorModifyingPantryIngredientMessage}</strong>
                            </Toast.Header>
                        </Toast>

                        {/* Success Toast Notifications */}
                        <div style={{
                            position: 'fixed',
                            top: '10px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 9999,
                            width: '50%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px'
                        }}>
                            {toastMessages.map(toast => (
                                <Toast 
                                    key={toast.id}
                                    show={true}
                                    onClose={() => setToastMessages(prev => prev.filter(t => t.id !== toast.id))}
                                    style={{
                                        backgroundColor: '#52cd92',
                                        border: 'none',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        width: '100%'
                                    }}
                                >
                                    <Toast.Header style={{ 
                                        backgroundColor: '#52cd92', 
                                        borderBottom: '1px solid rgba(255,255,255,0.2)',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        position: 'relative',
                                        width: '100%'
                                    }}>
                                        <strong style={{ color: 'white' }}>{toast.message}</strong>
                                    </Toast.Header>
                                </Toast>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </ErrorBoundary>
    );
};

export default PantryIngredients;
